// The privacy policy, as data. The page (pages/LegalPage.jsx) lays it out; to offer it in another language, add privacy.<lang>.js
// with the same shape and nothing else needs to change. `**word**` is shown in bold. Update `updated` whenever the text changes.
const privacy = {
  slug: 'privacy',
  kicker: 'Juridisk',
  title: 'Privatlivspolitik',
  lead: 'Her kan du læse, hvilke oplysninger NordComply behandler, hvorfor, hvor længe, og hvad du kan bede om.',
  updated: 'Sidst opdateret 17. oktober 2026',
  scope: 'Gælder for nordcomply.dk og NordComply-portalen',
  summaryTitle: 'I korte træk',
  summary: [
    'Vi læser jeres Microsoft 365-opsætning med læseadgang. Vi ændrer intet i den.',
    'Data opbevares hos Microsoft i Sverige.',
    'Vi bruger ingen reklamer og ingen analyseværktøjer, og vi sælger ikke data.',
  ],
  tocTitle: 'På denne side',
  sections: [
    {
      id: 'hvem',
      title: 'Hvem er vi',
      blocks: [
        { p: 'NordComply er en tjeneste, der giver virksomheder indblik i deres egen Microsoft 365-opsætning. Du kan altid kontakte os på support@nordcomply.dk.' },
        { p: 'Vi har to roller. For de brugere, der logger ind i NordComply (navn, e-mail, login og brug), er vi **dataansvarlig**. For de oplysninger, vi læser fra en kundes Microsoft 365, er vi **databehandler** for kunden og behandler dem efter kundens instruks og en databehandleraftale.' },
      ],
    },
    {
      id: 'oplysninger',
      title: 'Hvilke oplysninger behandler vi',
      blocks: [
        { p: 'Vi behandler kun det, der skal til for at levere tjenesten. Vi læser ikke indholdet af mails, filer eller samtaler. Vi læser kun opsætningen, fx hvilke regler der videresender mails.' },
        {
          table: {
            head: ['Oplysning', 'Eksempler', 'Formål og grundlag', 'Hvor længe'],
            rows: [
              ['Brugerkonto', 'Navn, e-mail, stilling, rolle og hvilke kunder du har adgang til', 'At give dig adgang til tjenesten. Aftalen med dig eller din arbejdsgiver.', 'Så længe kontoen findes'],
              ['Login og hændelser', 'Tidspunkt, hvordan du loggede ind, IP-adresse og hvad der blev oprettet, ændret eller fjernet', 'Sikkerhed og sporbarhed. Vores legitime interesse.', '12 måneder'],
              ['Aktiv tid', 'Hvor mange minutter og sessioner pr. dag du har arbejdet i tjenesten, og hvornår du sidst var aktiv. Kun NordComplys ejer kan se det.', 'At forstå, om og hvordan tjenesten bruges, og at hjælpe dig bedre. Vores legitime interesse.', '12 måneder'],
              ['Microsoft 365-data', 'Brugere, grupper, enheder, apps, loginregler, sikkerhedsindstillinger og tidspunkt for seneste login, læst via Microsoft Graph med læseadgang', 'At vise kunden sin opsætning og hvad der kræver handling. Vi er databehandler for kunden.', 'Den seneste aflæsning overskrives løbende. Historik over fund gemmes i 12 måneder'],
              ['Beslutninger og noter', 'Det, du skriver, hvem der skrev det og hvornår', 'At dokumentere kundens beslutninger. Aftalen med kunden.', 'Så længe kunden bruger tjenesten'],
              ['Henvendelser', 'Feedback og mails til support', 'At svare dig. Vores legitime interesse.', 'Så længe det er nødvendigt for at besvare dem'],
            ],
          },
        },
        { p: 'Når en kunde fjernes fra tjenesten, skjules den med det samme, og kundens data slettes endeligt i vores system efter 7 dage: aflæsninger, historik, beslutninger, noter, IT-opgaver og rapportplan. Brugere, der kun havde adgang til den kunde, fjernes også. Den, der fjernede kunden, får en bekræftelse på mail, både når kunden er fjernet, og når alt er slettet.' },
      ],
    },
    {
      id: 'modtagere',
      title: 'Hvem modtager data',
      blocks: [
        { p: 'Vi bruger få leverandører, og de behandler kun data for os:' },
        {
          ul: [
            '**Microsoft** leverer lagring og drift (Azure i Sverige), login (Entra ID), adgang til jeres Microsoft 365-data (Graph) og afsendelse af mails fra tjenesten.',
            '**Cloudflare** viser hjemmesidens sider og beskytter dem mod angreb. Her behandles IP-adresse, land og hvilke sider der åbnes. Jeres data (Microsoft 365-data, beslutninger og noter) går ikke gennem Cloudflare. De sendes direkte mellem din browser og vores server hos Microsoft i Sverige.',
          ],
        },
        { p: 'Vi videregiver ikke oplysninger til andre, medmindre loven kræver det.' },
        { p: 'Jeres data opbevares hos Microsoft i Sverige. Login hos Microsoft og Cloudflares beskyttelse af hjemmesiden kan i begrænset omfang behandle oplysninger uden for EU, fx i USA. Det gælder kun IP-adresse, land og login, ikke jeres Microsoft 365-data. Begge er certificeret under EU-US Data Privacy Framework, og vi har indgået EU-Kommissionens standardkontrakter med dem, så oplysningerne har samme beskyttelse som i EU.' },
      ],
    },
    {
      id: 'sikkerhed',
      title: 'Sikkerhed',
      blocks: [
        { p: 'Adgangen er opdelt, så en bruger kun kan se de kunder, vedkommende har adgang til. Al trafik er krypteret, og Microsoft krypterer lagrede data som standard. Login til tjenesten er kun muligt fra de lande, vi har åbnet for, og vi logger, hvem der gør hvad. Fjernede kunder ligger kun 7 dage i en papirkurv, før alt slettes endeligt.' },
      ],
    },
    {
      id: 'rettigheder',
      title: 'Dine rettigheder',
      blocks: [
        { p: 'Du kan bede om indsigt i de oplysninger, vi har om dig, få dem rettet eller slettet, begrænse eller gøre indsigelse mod behandlingen og få dem udleveret. Vi træffer ingen automatiske afgørelser om dig. Skriv til support@nordcomply.dk, så svarer vi hurtigst muligt.' },
        { note: '**Klage:** Du kan altid klage til Datatilsynet, datatilsynet.dk, hvis du mener, vi behandler dine oplysninger forkert.' },
      ],
    },
    {
      id: 'aendringer',
      title: 'Ændringer',
      blocks: [
        { p: 'Vi opdaterer politikken, når tjenesten eller reglerne ændrer sig. Datoen øverst viser, hvornår den sidst blev ændret. Større ændringer skriver vi til dig om.' },
      ],
    },
  ],
};

export default privacy;
